import { Injectable, inject, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {Store, Action} from '@ngrx/store';
import {catchError, concatMap, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, of} from 'rxjs';
import { FirmWareInfoActionTypes, FirmWareInfoActions } from '../actions/firm-ware-info.actions';
import * as fromStore from '../../../store';
import * as featureActions from '../../index';
import { FirmWareLogParams } from '../firm-ware.model';
import {FirmWareService} from '../../../services/firm-ware.service';
import { AzureBlobStorageParam } from '../../azure-blob-storage/azure-blob-storage.model';
import {UpdateInfoModel} from '../../updateInfo/update-info.model';
import {updateInfoType, containerType, FirmwareType, deviceTypeCodeRMCWithFile_dct} from '../../../shareModel';
import {TranslateService} from '@ngx-translate/core';
import { AccountListFilter } from '../../account/account.model';


@Injectable()
export class FirmWareInfoEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store<fromStore.State>);
  private firmWareService = inject(FirmWareService);
  private injector = inject(Injector);


  constructor() {}

  getFirmWares$ = createEffect(() => this.actions$.pipe(
    ofType(FirmWareInfoActionTypes.GetFirmWares),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]) => {
      const params = {
        otp: user.otp
      };
      return this.firmWareService.ReadFirmware(params).pipe(
        map((res) => {
          if (res.result && Array.isArray(res.msg.body)) {
            return new featureActions.LoadFirmWares(res.msg.body);
          } else {
            return new featureActions.GetFirmWaresFail();
          }
        }),
        // TODO: catchError Message調整 --> 留待未來再回頭定義Error Message
        catchError(error => {
          return of (new featureActions.GetFirmWaresFail());
        })
      );
    })
  ));
  getFirmwareInfo$ = createEffect(() => this.actions$.pipe(
    ofType(FirmWareInfoActionTypes.GetFirmwareInfo),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      const params = {
        otp: user.otp,
        id: action.payload
      };
      return this.firmWareService.ReadFirmwareInfo(params).pipe(
        map((res) => {
          if (res.result && Array.isArray(res.msg.body)) {
            return new featureActions.LoadFirmwareInfo(res.msg.body);
          } else {
            return new featureActions.GetFirmwareInfoFail();
          }
        }),
        // TODO: catchError Message調整 --> 留待未來再回頭定義Error Message
        catchError(error => {
          return of (new featureActions.GetFirmwareInfoFail());
        })
      );
    })
  ));
  getFirmwareList$ = createEffect(() => this.actions$.pipe(
    ofType(FirmWareInfoActionTypes.GetFirmwareList),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      const params = {
        otp: user.otp,
        id_list: null,
        FWType: action.payload.FWType
      };
      if (action.payload.ids && action.payload.ids.length > 0) {
        params.id_list = action.payload.ids;
      }
      return this.firmWareService.ReadFirmwareInfoList(params).pipe(
        switchMap((res) => {
          if (res.result && Array.isArray(res.msg.body)) {
            const idList = res.msg.body.map(data => data.UpdateId);
            const filter = {Acodes: idList} as AccountListFilter;
            return [new featureActions.LoadFirmwareList(res.msg.body),
                    // new featureActions.GetAccountList(idList),
                    new featureActions.SelectAccountListFilters(filter)];
          } else {
            return [new featureActions.GetFirmwareListFail()];
          }
        }),
        // TODO: catchError Message調整 --> 留待未來再回頭定義Error Message
        catchError(error => {
          return [new featureActions.GetFirmWaresFail()];
        })
      );
    })
  ));
  getAddFirmwareInfo$ = createEffect(() => this.actions$.pipe(
    ofType(FirmWareInfoActionTypes.GetAddFirmwareInfo),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      // const containerName = azureBlobStorage.containerName;
      const timeStamp = new Date().getTime().toString().substr(0, 10);
      const versionStr = action.payload.firmwareInfo.version.replace(/\./g, '');
      const path = '/' + containerType.Firmware + '/' + versionStr + '/' + timeStamp + '/' + action.payload.firmwareInfo.fileName;
      const path_dct = '/' + containerType.Firmware + '/' + versionStr + '/' + timeStamp + '/' + action.payload.firmwareInfo.file_dctName;
      const param = {
        otp: user.otp,
        version: action.payload.firmwareInfo.version,
        FWType: action.payload.firmwareInfo.FWType,
        type: action.payload.firmwareInfo.type,
        region: action.payload.firmwareInfo.region,
        path: path,
        note: action.payload.firmwareInfo.note,
        critical: null,
        path_dct: null,
        Model: action.payload.firmwareInfo.Model
      };
      if (action.payload.firmwareInfo.FWType === FirmwareType.RMC) {
        if (deviceTypeCodeRMCWithFile_dct.includes(action.payload.firmwareInfo.type)) {
          param.path_dct = path_dct;
        }
      } else {
        param.critical = action.payload.firmwareInfo.critical;
      }
      const FileParam: AzureBlobStorageParam = {
        name: action.payload.firmwareInfo.fileName,
        path: versionStr,
        file: action.payload.firmwareInfo.file,
        timestamp: timeStamp,
        container: containerType.Firmware
      };
      const File_dctParam: AzureBlobStorageParam = {
        name: action.payload.firmwareInfo.file_dctName,
        path: versionStr,
        file: action.payload.firmwareInfo.file_dct,
        timestamp: timeStamp,
        container: containerType.Firmware
      };
      return this.firmWareService.AddFirmwareInfo(param).pipe(
        switchMap((res) => {
          const updateRes: UpdateInfoModel = {
            result: false,
            msg: '',
            errmsg: 'Error',
            type: updateInfoType.AddFirmwareUpload
          };
          if (res.Flag) {
            const idParam: number[] = null;
            const MgtLogParam: FirmWareLogParams = { FWType: action.payload.firmwareInfo.FWType };
            const successRes = {
              ...updateRes,
              result: true,
              msg: this.injector.get(TranslateService).instant('Update Success')
            };
            const actions: Action[] = [
              new featureActions.GetFirmwareList({ ids: idParam, FWType: action.payload.firmwareInfo.FWType }),
              new featureActions.GetFirmwareMgtLogs(MgtLogParam),
              new featureActions.UpdateInfoSuccessAction(successRes)
            ];
            if (FileParam.file) {
              actions.push(new featureActions.GetUploadAzureBlobStorages(FileParam));
            }
            if (action.payload.firmwareInfo.FWType === FirmwareType.RMC &&
              deviceTypeCodeRMCWithFile_dct.includes(action.payload.firmwareInfo.type) && File_dctParam.file) {
                actions.push(new featureActions.GetUploadAzureBlobStorages(File_dctParam));
            }
            return actions;
          } else {
            return [new featureActions.AddFirmwareInfoFail(),
                    new featureActions.UpdateInfoFailAction({ ...updateRes, errmsg: this.injector.get(TranslateService).instant('Update Failed') })];
          }
        }),
        // TODO: catchError Message調整 --> 已完成
        catchError(error => {
          const updateRes: UpdateInfoModel = {
            result: false,
            errmsg: 'Error',
            type: updateInfoType.AddFirmwareUpload
          };
          return [new featureActions.AddFirmwareInfoFail(),
                  new featureActions.UpdateInfoFailAction(updateRes)];
        })
      );
    })
  ));
  getUpdateFirmwareInfo$ = createEffect(() => this.actions$.pipe(
    ofType(FirmWareInfoActionTypes.GetUpdateFirmwareInfo),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      // const containerName = azureBlobStorage.containerName;
      const timeStamp = action.payload.firmwareInfo.timeStamp ?
       action.payload.firmwareInfo.timeStamp : new Date().getTime().toString().substr(0, 10);
      const versionStr = action.payload.firmwareInfo.version.replace(/\./g, '');
      const path = action.payload.firmwareInfo.path ? action.payload.firmwareInfo.path :
        ('/' + containerType.Firmware + '/' + versionStr + '/' + timeStamp + '/' + action.payload.firmwareInfo.fileName);
      const path_dct = action.payload.firmwareInfo.path_dct ? action.payload.firmwareInfo.path_dct :
        ('/' + containerType.Firmware + '/' + versionStr + '/' + timeStamp + '/' + action.payload.firmwareInfo.file_dctName);
      const param = {
        otp: user.otp,
        id: action.payload.firmwareInfo.id,
        path: path,
        note: action.payload.firmwareInfo.note,
        critical: null,
        path_dct: null,
        FWType: action.payload.firmwareInfo.FWType
      };
      if (action.payload.firmwareInfo.FWType === FirmwareType.RMC) {
        if (deviceTypeCodeRMCWithFile_dct.includes(action.payload.firmwareInfo.type)) {
          param.path_dct = path_dct;
        }
      } else {
        param.critical = action.payload.firmwareInfo.critical;
      }
      const FileParam: AzureBlobStorageParam = {
        name: action.payload.firmwareInfo.fileName,
        path: versionStr,
        file: action.payload.firmwareInfo.file,
        timestamp: timeStamp,
        container: containerType.Firmware
      };
      const File_dctParam: AzureBlobStorageParam = {
        name: action.payload.firmwareInfo.file_dctName,
        path: versionStr,
        file: action.payload.firmwareInfo.file_dct,
        timestamp: timeStamp,
        container: containerType.Firmware
      };
      return this.firmWareService.UpdateFirmwareInfo(param).pipe(
        switchMap((res) => {
          const updateRes: UpdateInfoModel = {
            result: false,
            msg: '',
            errmsg: 'Error',
            type: updateInfoType.EditFirmwareUpload
          };
          if (res.Flag) {
            const idParam: number[] = null;
            const MgtLogParam: FirmWareLogParams = { FWType: action.payload.firmwareInfo.FWType };
            const successRes = {
              ...updateRes,
              result: true,
              msg: this.injector.get(TranslateService).instant('Update Success')
            };
            const actions: Action[] = [
              new featureActions.GetFirmwareList({ ids: idParam, FWType: action.payload.firmwareInfo.FWType }),
              new featureActions.GetFirmwareMgtLogs(MgtLogParam),
              new featureActions.UpdateInfoSuccessAction(successRes)
            ];
            if (FileParam.file) {
              actions.push(new featureActions.GetUploadAzureBlobStorages(FileParam));
            }
            if (action.payload.firmwareInfo.FWType === FirmwareType.RMC &&
              deviceTypeCodeRMCWithFile_dct.includes(action.payload.firmwareInfo.type) && File_dctParam.file) {
                actions.push(new featureActions.GetUploadAzureBlobStorages(File_dctParam));
            }
            return actions;
          } else {
            return [new featureActions.UpdateFirmwareInfoFail(),
                    new featureActions.UpdateInfoFailAction({ ...updateRes, errmsg: this.injector.get(TranslateService).instant('Update Failed') })];
          }
        }),
        // TODO: catchError Message調整 --> 已完成
        catchError(error => {
          const updateRes: UpdateInfoModel = {
            result: false,
            errmsg: 'Error',
            type: updateInfoType.EditFirmwareUpload
          };
          return [new featureActions.UpdateFirmwareInfoFail(),
                  new featureActions.UpdateInfoFailAction(updateRes)];
        })
      );
    })
  ));
  getReleaseFirmwareInfo$ = createEffect(() => this.actions$.pipe(
    ofType(FirmWareInfoActionTypes.GetReleaseFirmwareInfo),
    withLatestFrom(this.store$.select(fromStore.getCurrentUser)),
    switchMap(([action, user]: [any, any]) => {
      const param = {
        otp: user.otp,
        id: action.payload.firmwareInfo.id,
        FWType: action.payload.firmwareInfo.FWType
      };
      return this.firmWareService.ReleaseFirmwareInfo(param).pipe(
        switchMap((res) => {
          const updateRes: UpdateInfoModel = {
            result: false,
            msg: '',
            errmsg: 'Error',
            type: updateInfoType.ReleaseFirmwareUpload
          };
          if (res.Flag) {
            const idParam: number[] = null;
            const MgtLogParam: FirmWareLogParams = { FWType: action.payload.firmwareInfo.FWType };
            return [new featureActions.GetFirmwareList({ ids: idParam, FWType: action.payload.firmwareInfo.FWType }),
                    new featureActions.GetFirmwareMgtLogs(MgtLogParam),
                    new featureActions.UpdateInfoSuccessAction({ ...updateRes, result: true, msg: this.injector.get(TranslateService).instant('Update Success') })];
          } else {
            return [new featureActions.ReleaseFirmwareInfoFail(),
                    new featureActions.UpdateInfoFailAction({ ...updateRes, errmsg: this.injector.get(TranslateService).instant('Update Failed') })];
          }
        }),
        // TODO: catchError Message調整 --> 已完成
        catchError(error => {
          const updateRes: UpdateInfoModel = {
            result: false,
            errmsg: 'Error',
            type: updateInfoType.ReleaseFirmwareUpload
          };
          return [new featureActions.ReleaseFirmwareInfoFail(),
                  new featureActions.UpdateInfoFailAction(updateRes)];
        })
      );
    })
  ));

}
